import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Paper, TextField, Button, Typography, Box, Alert } from '@mui/material';
import axios from 'axios';

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing setup token.');
      setTokenValid(false);
      return;
    }

    // Verify token and get user info
    const verifyToken = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-setup-token/${token}`);
        setUserInfo(response.data.user);
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired setup token.');
        setTokenValid(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post('/api/auth/setup-password', { token, password });
      setSuccess('Your password has been set successfully! You can now log in.');
      setTimeout(
        () =>
          navigate('/login', {
            state: {
              message:
                'Your password is set. Log in with your email and new password to continue your vendor registration.',
            },
          }),
        3000
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: '#1a237e', fontWeight: 600 }}>
              Invalid Link
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography align="center" sx={{ mb: 3 }}>
              This setup link may have expired or is invalid. Please contact an administrator for a new invitation.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1',
                },
                py: 1.5,
                fontSize: '1.1rem',
                color: '#ffffff',
              }}
            >
              Go to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, backgroundColor: '#ffffff' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: '#1a237e', fontWeight: 600 }}>
            Set Up Your Password
          </Typography>
          {userInfo && (
            <Typography align="center" sx={{ mb: 2, color: 'text.secondary' }}>
              Welcome, {userInfo.firstName} {userInfo.lastName}! Please set up your password to complete your account setup.
            </Typography>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          )}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="New Password"
              name="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              helperText="Password must be at least 8 characters long"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1',
                },
                py: 1.5,
                fontSize: '1.1rem',
                color: '#ffffff',
              }}
            >
              {loading ? 'Setting Up...' : 'Set Up Password'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default SetupPassword;
